/**
 * THE BACKUP VAULT, on the person's device — the only place its secrets ever exist.
 *
 *   owner, 2026-09-29: 讓使用者可以給他一個專屬的 Vault … 我們要讓他知道他的 password
 *   我們是不保存的, 丟失了就沒有辦法 recover.
 *
 * WHAT A VAULT IS
 *
 *   A P-256 key pair. Machines encrypt backups to its PUBLIC key (edge backupCrypto.ts),
 *   so backing up needs no secret; opening a backup needs the PRIVATE key.
 *
 *   The private key is stored by the cloud only encrypted, under a random 32-byte vault
 *   key (VK). And the VK is stored only wrapped, twice:
 *     pw  under PBKDF2-SHA256(password,      600 000 iterations)
 *     rc  under PBKDF2-SHA256(recovery code, 100 000 iterations — the code is 160 random
 *         bits, so the iterations are not what protects it)
 *   Both derivations run HERE. The password and the recovery code are never sent,
 *   stored or logged anywhere — which is why losing both loses the backups, and why
 *   a new password with the recovery code keeps them (it re-wraps the SAME VK).
 *
 * FORMAT v1 — a restore on another device, years on, reads exactly this:
 *   enc_vsk  AES-256-GCM(VK, iv, PKCS8 private key, aad "avengents-vault-v1:vsk:<vpk>")
 *   pw / rc  {salt 16, iter, iv 12, ct = AES-256-GCM(kdf, iv, VK, aad "avengents-vault-v1:pw"|":rc")}
 *   vpk      the 65-byte uncompressed public point
 *   all standard base64.
 *
 * WebCrypto only: the platform's own implementation, in the browser and in the iOS
 * shell's WebView alike — no crypto code of ours ships (App Store export compliance
 * turns on that; see the app-store-release notes before shipping any other kind).
 */

export type VaultWrap = { salt: string; iter: number; iv: string; ct: string }
export type Vault = {
  v?: number
  vpk: string
  enc_vsk: { iv: string; ct: string }
  pw: VaultWrap
  rc: VaultWrap
  /** Face ID / Touch ID: one wrap per passkey (passkey.ts). Absent on a vault without one. */
  pk?: PasskeyWrap[]
}

/**
 * THE VAULT KEY UNDER A PASSKEY — owner, 2026-10-01 (option 1, probe passed 10-02).
 * The passkey's PRF output (released only by Face ID / Touch ID) → HKDF-SHA256(salt, info
 * "avengents-vault-v1:pk") → AES-GCM over the SAME vault key, aad binding the credential id.
 * A third door beside the password and the recovery code; neither of those changes.
 */
export type PasskeyWrap = { cred: string; salt: string; iv: string; ct: string; at?: number }

const PW_ITER = 600_000
const RC_ITER = 100_000
/** Long enough that an offline guesser holding the wrapped key has real work to do. */
export const MIN_PASSWORD = 10

const subtle = () => globalThis.crypto.subtle
/*
 * Every byte array here is Uint8Array<ArrayBuffer>: WebCrypto's BufferSource refuses one
 * that might sit on a SharedArrayBuffer, which a bare Uint8Array could.
 */
type Bytes = Uint8Array<ArrayBuffer>
const utf8 = (s: string): Bytes => new Uint8Array(new TextEncoder().encode(s))
const rand = (n: number): Bytes => globalThis.crypto.getRandomValues(new Uint8Array(n))

export function b64(u: ArrayBuffer | Uint8Array): string {
  const a = u instanceof Uint8Array ? u : new Uint8Array(u)
  let s = ''
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]!)
  return btoa(s)
}

function unb64(s: string): Bytes {
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** The password or code did not open the vault — the only thing a wrong secret can do. */
export class WrongSecret extends Error {
  constructor(what: 'password' | 'recovery code' | 'passkey') {
    super(`That ${what} does not open this vault.`)
    this.name = 'WrongSecret'
  }
}

/** The vault opened but its parts do not belong together — stored data was damaged. */
class VaultDamaged extends Error {
  constructor() {
    super("This vault's stored keys do not match each other.")
    this.name = 'VaultDamaged'
  }
}

// ── the recovery code ─────────────────────────────────────────────────────────

/** Crockford's base32: no I, L, O or U, so nothing reads as something else. */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** 160 random bits as eight groups of four: XXXX-XXXX-…-XXXX. */
function newRecoveryCode(): string {
  const bytes = rand(20)
  let bits = 0
  let acc = 0
  let out = ''
  for (const b of bytes) {
    acc = (acc << 8) | b
    bits += 8
    while (bits >= 5) {
      out += CROCKFORD[(acc >>> (bits - 5)) & 31]
      bits -= 5
    }
  }
  return out.match(/.{4}/g)!.join('-')
}

/**
 * What the person typed, as the code it was: case, spaces and dashes do not matter,
 * and the letters Crockford leaves out are read as the digits they look like.
 */
function normalizeRecoveryCode(s: string): string {
  return s
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
}

// ── key derivation and wrapping ───────────────────────────────────────────────

async function kdf(secret: string, salt: Bytes, iter: number, use: 'encrypt' | 'decrypt'): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', utf8(secret), 'PBKDF2', false, ['deriveKey'])
  return subtle().deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: iter },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    [use],
  )
}

async function wrap(vk: Bytes, secret: string, iter: number, label: 'pw' | 'rc'): Promise<VaultWrap> {
  const salt = rand(16)
  const iv = rand(12)
  const key = await kdf(secret, salt, iter, 'encrypt')
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: utf8(`avengents-vault-v1:${label}`) }, key, vk)
  return { salt: b64(salt), iter, iv: b64(iv), ct: b64(ct) }
}

async function unwrap(w: VaultWrap, secret: string, label: 'pw' | 'rc'): Promise<Bytes> {
  const key = await kdf(secret, unb64(w.salt), w.iter, 'decrypt')
  try {
    return new Uint8Array(
      await subtle().decrypt(
        { name: 'AES-GCM', iv: unb64(w.iv), additionalData: utf8(`avengents-vault-v1:${label}`) },
        key,
        unb64(w.ct),
      ),
    )
  } catch {
    throw new WrongSecret(label === 'pw' ? 'password' : 'recovery code')
  }
}

const vskAad = (vpk: string) => utf8(`avengents-vault-v1:vsk:${vpk}`)

/** A new vault for `password`, and the recovery code to show the person ONCE. */
export async function createVault(password: string): Promise<{ vault: Vault; recoveryCode: string }> {
  const kp = (await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits'])) as CryptoKeyPair
  const vpk = b64(await subtle().exportKey('raw', kp.publicKey))
  const vsk = new Uint8Array(await subtle().exportKey('pkcs8', kp.privateKey))
  const vk = rand(32)
  const vkKey = await subtle().importKey('raw', vk, { name: 'AES-GCM' }, false, ['encrypt'])
  const iv = rand(12)
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: vskAad(vpk) }, vkKey, vsk)
  vsk.fill(0)
  const recoveryCode = newRecoveryCode()
  const vault: Vault = {
    v: 1,
    vpk,
    enc_vsk: { iv: b64(iv), ct: b64(ct) },
    pw: await wrap(vk, password, PW_ITER, 'pw'),
    rc: await wrap(vk, normalizeRecoveryCode(recoveryCode), RC_ITER, 'rc'),
  }
  vk.fill(0)
  return { vault, recoveryCode }
}

/**
 * The vault's private key from its vault key — and proof the vault is whole: the
 * private key must be the one whose public half the machines encrypt to.
 */
export async function openVault(vault: Vault, vk: Bytes): Promise<CryptoKey> {
  const vkKey = await subtle().importKey('raw', vk, { name: 'AES-GCM' }, false, ['decrypt'])
  let pkcs8: ArrayBuffer
  try {
    pkcs8 = await subtle().decrypt(
      { name: 'AES-GCM', iv: unb64(vault.enc_vsk.iv), additionalData: vskAad(vault.vpk) },
      vkKey,
      unb64(vault.enc_vsk.ct),
    )
  } catch {
    throw new VaultDamaged()
  }
  const priv = await subtle().importKey('pkcs8', pkcs8, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits'])
  const jwk = await subtle().exportKey('jwk', priv)
  const pub = unb64(vault.vpk)
  const x = b64url(pub.subarray(1, 33))
  const y = b64url(pub.subarray(33, 65))
  if (jwk.x !== x || jwk.y !== y) throw new VaultDamaged()
  return subtle().importKey('pkcs8', pkcs8, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits'])
}

const b64url = (u: Uint8Array) => b64(u).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

/** The vault key, from the password. Throws WrongSecret. */
export async function unlockWithPassword(vault: Vault, password: string): Promise<Bytes> {
  const vk = await unwrap(vault.pw, password, 'pw')
  await openVault(vault, vk)
  return vk
}

/** The vault key, from the recovery code. Throws WrongSecret. */
export async function unlockWithRecoveryCode(vault: Vault, code: string): Promise<Bytes> {
  const vk = await unwrap(vault.rc, normalizeRecoveryCode(code), 'rc')
  await openVault(vault, vk)
  return vk
}

/** A new password wrap for the SAME vault key — what "forgot my password" sends. */
export const passwordWrap = (vk: Bytes, password: string): Promise<VaultWrap> => wrap(vk, password, PW_ITER, 'pw')

async function pkKey(prf: Bytes, salt: Bytes, use: 'encrypt' | 'decrypt'): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', prf, 'HKDF', false, ['deriveKey'])
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: utf8('avengents-vault-v1:pk') },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    [use],
  )
}

/** The vault key wrapped under one passkey's PRF output. */
export async function passkeyWrap(vk: Bytes, prf: Bytes, cred: string): Promise<PasskeyWrap> {
  const salt = rand(16)
  const iv = rand(12)
  const key = await pkKey(prf, salt, 'encrypt')
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: utf8(`avengents-vault-v1:pk:${cred}`) }, key, vk)
  return { cred, salt: b64(salt), iv: b64(iv), ct: b64(ct) }
}

/** The vault key, from the PRF output of the passkey `cred`. Throws WrongSecret. */
export async function unlockWithPasskeyPrf(vault: Vault, cred: string, prf: Bytes): Promise<Bytes> {
  const w = (vault.pk ?? []).find((x) => x.cred === cred)
  if (!w) throw new WrongSecret('passkey')
  const key = await pkKey(prf, unb64(w.salt), 'decrypt')
  let vk: Bytes
  try {
    vk = new Uint8Array(
      await subtle().decrypt({ name: 'AES-GCM', iv: unb64(w.iv), additionalData: utf8(`avengents-vault-v1:pk:${cred}`) }, key, unb64(w.ct)),
    )
  } catch {
    throw new WrongSecret('passkey')
  }
  await openVault(vault, vk)
  return vk
}

// ── restore: this device opens the backup's key and re-seals it to the machine ──────────

type Sealed = { eph: string; iv: string; ct: string }

/**
 * The one sealed box (edge backupCrypto.ts): a fresh P-256 pair per seal; ECDH with the
 * recipient; HKDF-SHA256(shared, salt = eph_raw ‖ recipient_raw, info); AES-256-GCM.
 */
async function kekFor(shared: ArrayBuffer, ephRaw: Bytes, pubRaw: Bytes, info: string, use: 'encrypt' | 'decrypt') {
  const salt = new Uint8Array(ephRaw.length + pubRaw.length)
  salt.set(ephRaw, 0)
  salt.set(pubRaw, ephRaw.length)
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: utf8(info) },
    await subtle().importKey('raw', shared, 'HKDF', false, ['deriveKey']),
    { name: 'AES-GCM', length: 256 },
    false,
    [use],
  )
}

/** The backup's key, from its seal to the vault — with the vault's private key. */
export async function unwrapBackupKey(sealed: Sealed, vaultPrivate: CryptoKey, vpk: string, backupId: string): Promise<Bytes> {
  const ephRaw = unb64(sealed.eph)
  const eph = await subtle().importKey('raw', ephRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, [])
  const shared = await subtle().deriveBits({ name: 'ECDH', public: eph }, vaultPrivate, 256)
  const kek = await kekFor(shared, ephRaw, unb64(vpk), 'avengents-backup-v1:dek', 'decrypt')
  return new Uint8Array(
    await subtle().decrypt(
      { name: 'AES-GCM', iv: unb64(sealed.iv), additionalData: utf8(`avengents-backup-v1:${backupId}`) },
      kek,
      unb64(sealed.ct),
    ),
  )
}

async function sealTo(secret: Bytes, pubB64: string, info: string, aad: string): Promise<Sealed> {
  const pubRaw = unb64(pubB64)
  const pub = await subtle().importKey('raw', pubRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, [])
  const eph = (await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits'])) as CryptoKeyPair
  const ephRaw = new Uint8Array(await subtle().exportKey('raw', eph.publicKey))
  const shared = await subtle().deriveBits({ name: 'ECDH', public: pub }, eph.privateKey, 256)
  const kek = await kekFor(shared, ephRaw, pubRaw, info, 'encrypt')
  const iv = rand(12)
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: utf8(aad) }, kek, secret)
  return { eph: b64(ephRaw), iv: b64(iv), ct: b64(ct) }
}

/** The backup's key sealed to the restoring machine's one-time key — all the relay carries. */
export const sealForMachine = (dek: Bytes, epk: string, restoreId: string): Promise<Sealed> =>
  sealTo(dek, epk, 'avengents-restore-v1:dek', `avengents-restore-v1:${restoreId}`)

/**
 * The backup's key sealed to a FRIEND'S vault — owner, 2026-09-30: share a backup with a
 * friend under the friend's own vault. Exactly the seal the machine makes to the owner's
 * vault at backup time (edge backupCrypto.wrapDek), so the friend's restore opens it with
 * nothing new. The backup id is the same in the friend's copy: every part is bound to it.
 */
export const sealToVault = (dek: Bytes, vpk: string, backupId: string): Promise<Sealed> =>
  sealTo(dek, vpk, 'avengents-backup-v1:dek', `avengents-backup-v1:${backupId}`)

